import { useDeepCompareEffect } from 'ahooks';
import classNames from 'classnames';
import { cloneDeep } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Button, FontIconBtn, Message, SliderInput, Tabs, Tooltip } from '@meitu/saas-ui';
import { ITabItemProps } from '@meitu/saas-ui/es/tabs/interface';
import { formatColor } from '@meitu/saas-colors';
import { base64Kit } from '@meitu/saas-editor-core/es/utils/base64-kit';
import { getImgInfoAsync, imageConverter, loadImageAsync } from '@meitu/saas-editor-core/es/utils/file-kit';
import { computerBgRatio } from '@meitu/saas-editor-core/es/utils/layer-kit';
import {
    CanvasType,
    GradientColorResult,
    IBackgroundLayerProps,
    IDynamicInfoProps,
    IImageTransform,
    ILayerProps,
    StretchOptionType,
} from '@meitu/saas-editor-core/es/version-types';
import { useMultiSelectState } from '@meitu/saas-editor-ui/es/collapses/navigation/hooks/use-stage-event';
import ImageUpload from '@meitu/saas-editor-ui/es/components/image-upload';
import LabelAction from '@meitu/saas-editor-ui/es/components/label-action';
import IconFont from '@meitu/xdesign-icon-font';
import { ColorGroup } from '@meitu/xdesign-widgets';
import useFilesUpload from '@/hooks/use-files-upload';
import { getLocaleStore, useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { isIntl } from '@/react-process-env';
import { useEditorModel } from '@/store/models/editor-model';
import { useTemplateModel } from '@/store/models/template-model';
import { IEditorType } from '@/types';
import DynamicBg from '../dynamic-bg/dynamic-bg';

export const IsFromOtherEntrance = 'IsFromOtherEntrance';

enum TAB_KEY {
    COLOR = '1',
    IMAGE = '2',
    DYNAMIC = '3',
}

interface IPreviewWrapProps {
    url: string;
}

interface IBackgroundBtnsProps {
    layer: ILayerProps;
    setCurrent: Function;
    currentWidth: number;
    currentHeight: number;
    selectedIndexes?: number[];
}

interface IUploadBackgroundProps extends Omit<IBackgroundBtnsProps, 'layer'> {
    card: boolean;
}

interface IBackgroundImg {
    id: number;
    url: string;
    stretchOption: StretchOptionType;
    imageTransform: IImageTransform;
    dynamicInfo?: IDynamicInfoProps;
}

interface IUpdateBackgroundImg {
    id?: number;
    url?: string;
    stretchOption?: StretchOptionType;
    imageTransform?: IImageTransform;
    dynamicInfo?: IDynamicInfoProps;
}

const overturns = () => {
    const { $t } = getLocaleStore();

    return [
        {
            name: $t((v) => v.panel.backgroundPanel.horizontalFlip),
            icon: 'FlipHorizontalBold',
            width: 16,
            handler: 'horizontal',
        },
        {
            name: $t((v) => v.panel.backgroundPanel.verticalFlip),
            icon: 'FlipVerticalBold',
            width: 16,
            handler: 'vertical',
        },
    ];
};

const PreviewWrap: React.FC<IPreviewWrapProps> = React.memo(({ url }) => {
    const { $t } = useLocaleStore();

    const handleClick = () => {
        const el = document.querySelector('.c-background-panel__opt-btn button') as HTMLButtonElement;
        el?.click();
    };

    return (
        <div className="c-background-panel__preview" onClick={handleClick}>
            {url ? (
                <>
                    <Button type="primary" icon={<IconFont name="RedrawBold" fontSize={16} />}>
                        {$t((v) => v.panel.backgroundPanel.replace)}
                    </Button>
                    <img src={imageConverter(base64Kit.getLocalUrl(url), 320)} alt="" />
                </>
            ) : null}
        </div>
    );
});

const BackgroundBtns: React.FC<IBackgroundBtnsProps> = React.memo(
    ({ layer, setCurrent, currentWidth, currentHeight, selectedIndexes }) => {
        const { $t } = useLocaleStore();
        const { opacity } = layer;
        const originOpacity = useRef<number | null>(null);
        const { stretchOption } = layer.data as IBackgroundLayerProps;
        const editorType = useEditorModel((state) => state.editorType);
        const setIsDisable = useEditorModel((state) => state.setIsDisable);

        useMultiSelectState(editor);

        useEffect(() => {
            originOpacity.current = null;
            const cropLayer = editor.cropBackground.getLatestLayer();
            if (cropLayer && cropLayer.id !== layer.id) editor.cropBackground.cancel();
        }, [layer.id]);

        useEffect(() => {
            // 裁剪插件负责画布显隐，面板只锁定操作入口。
            const handlers = [
                editor.cropBackground.onOpen(() => setIsDisable(true)),
                editor.cropBackground.onClose(() => setIsDisable(false)),
            ];
            return () => {
                if (editor.cropBackground.getLatestLayer()) editor.cropBackground.cancel();
                handlers.forEach((handler) => editor.off(handler));
            };
        }, [setIsDisable]);

        return (
            <div className="c-background-panel__opt-btn">
                <UploadBackground
                    card={false}
                    setCurrent={setCurrent}
                    currentWidth={currentWidth}
                    currentHeight={currentHeight}
                />
                <div className="c-background-panel__opt-btn-container">
                    {stretchOption === StretchOptionType.repeat ? (
                        <Tooltip title={$t((v) => v.panel.backgroundPanel.cropLimit)}>
                            <span>
                                <FontIconBtn name="CropBold" type="button" fontSize={16} disable />
                            </span>
                        </Tooltip>
                    ) : (
                        <Tooltip title={$t((v) => v.panel.backgroundPanel.crop)}>
                            <span>
                                <FontIconBtn
                                    name="CropBold"
                                    type="button"
                                    fontSize={16}
                                    onClick={() => {
                                        editor.cropBackground.open();
                                    }}
                                />
                            </span>
                        </Tooltip>
                    )}
                    {editorType !== IEditorType.imageEditor && (selectedIndexes?.length || 1) === 1 ? (
                        <Tooltip title={$t((v) => v.panel.backgroundPanel.splitBackground)}>
                            <span>
                                <FontIconBtn
                                    name="SeparateBackgroundBold"
                                    type="button"
                                    fontSize={16}
                                    onClick={() => {
                                        editor.backgroundLayer.convertBackgroundToImage();
                                        Message.success($t((v) => v.panel.backgroundPanel.tips.splitSuccess));
                                    }}
                                />
                            </span>
                        </Tooltip>
                    ) : null}
                    {overturns().map((v, i) => {
                        return (
                            <Tooltip title={v.name} key={`over-${i}`}>
                                <span>
                                    <FontIconBtn
                                        name={v.icon}
                                        fontSize={v.width}
                                        key={i}
                                        type="button"
                                        onClick={() => {
                                            v.handler === 'horizontal'
                                                ? editor.backgroundLayer.setHorizontal()
                                                : editor.backgroundLayer.setVertical();
                                        }}
                                    />
                                </span>
                            </Tooltip>
                        );
                    })}
                </div>

                {(selectedIndexes?.length || 1) === 1 && (
                    <LabelAction title={$t((v) => v.panel.backgroundPanel.opacity)}>
                        <div className="text-action__item">
                            <SliderInput
                                value={parseInt(`${opacity * 100}`)}
                                max={100}
                                min={0}
                                type="horizontal"
                                showStepBtn={true}
                                onChange={(val: number) => {
                                    if (originOpacity.current === null) {
                                        originOpacity.current = opacity;
                                    }
                                    editor.backgroundLayer.setOpacity(layer.id, val / 100);
                                }}
                                onAfterChange={(val: number) => {
                                    if (originOpacity.current !== null) {
                                        editor.backgroundLayer.setOpacity(layer.id, val / 100, originOpacity.current);
                                        originOpacity.current = null;
                                    }
                                }}
                            />
                        </div>
                    </LabelAction>
                )}
            </div>
        );
    },
);
const UploadBackground: React.FC<IUploadBackgroundProps> = React.memo(
    ({ setCurrent, card, currentWidth, currentHeight }) => {
        const { $t } = useLocaleStore();
        const { filesUpload } = useFilesUpload();

        const replaceImageProps: any = {
            children: (
                <>
                    <Button
                        type="primary"
                        icon={<IconFont name="UploadBold" fontSize={16} />}
                        style={card ? {} : { display: 'none' }}
                        data-blue
                    >
                        {$t((v) => v.panel.backgroundPanel.uploadImage)}
                    </Button>
                </>
            ),
            tips: '',
            tipsPlacement: 'top',
            onSuccess: async (url: string) => {
                const img = await loadImageAsync(url);
                const { width, height } = img;
                const image = { id: '', url, width, height, thumbnail: url };
                addMaterialCallback(image);
            },
        };
        useEffect(() => {
            const onPreventDefaultHandle = (ev: DragEvent) => {
                ev.preventDefault();
            };
            const onDropHandle = async (ev: DragEvent) => {
                ev.preventDefault();

                if (ev.dataTransfer?.files?.length) {
                    if (ev.dataTransfer.files.length > 1) {
                        Message.info($t((v) => v.panel.backgroundPanel.messageError.uploadNumLimit));
                    }
                    const files = Array.from(ev.dataTransfer?.files).slice(0, 1);
                    if (!files.length) return;
                    const image = (await filesUpload(files))?.[0];
                    await addMaterialCallback({
                        id: uuid(),
                        thumbnail: image.url,
                        width: image.width,
                        height: image.height,
                    });
                    editor.services.mask.hide();
                }
            };
            let imageContainer = document.querySelector('.c-background-panel .img-upload') as HTMLElement | null;
            imageContainer?.addEventListener('dragover', onPreventDefaultHandle, false);
            imageContainer?.addEventListener('drop', onDropHandle, false);
            return () => {
                imageContainer?.removeEventListener('dragover', onPreventDefaultHandle, false);
                imageContainer?.removeEventListener('drop', onDropHandle, false);
                imageContainer = null;
            };
        }, []);

        const addMaterialCallback = async (image: { id: string; thumbnail: string; width: number; height: number }) => {
            const { widthRatio, heightRatio } = computerBgRatio({
                imgWidth: image.width,
                imgHeight: image.height,
                stageHeight: currentHeight,
                stageWidth: currentWidth,
            });
            const imageTransform = {
                offsetX: 0,
                offsetY: 0,
                rotate: 0,
                scale: 1,
                flipH: false,
                flipV: false,
                widthRatio,
                heightRatio,
            };
            const stretchOption = StretchOptionType.cover;
            editor.backgroundLayer.setBackgroundUrl({
                url: image.thumbnail,
                backgroundColor: '',
                id: Number(image.id) || 0,
                naturalHeight: image.height,
                naturalWidth: image.width,
                imageTransform,
                stretchOption,
            });
            setCurrent({ url: image.thumbnail, id: image.id, imageTransform, stretchOption });
            editor.services.mask.hide();
        };

        return (
            <>
                {card ? (
                    <ImageUpload
                        card
                        messageText={
                            <>{$t((v, { n }) => n(v.panel.backgroundPanel.clickOrDropToUpload, [<br key="br" />]))}</>
                        }
                        {...replaceImageProps}
                    />
                ) : (
                    <ImageUpload {...replaceImageProps} />
                )}
            </>
        );
    },
);

const TabsRender: React.FC<{
    layer: ILayerProps | null;
    currentWidth: number;
    currentHeight: number;
    selectedIndexes: number[];
}> = ({ layer, currentWidth, currentHeight, selectedIndexes }) => {
    const { $t } = useLocaleStore();
    const materialId = editor.backgroundLayer.getBackgroundMaterialId(layer);
    const {
        backgroundColor,
        url,
        stretchOption,
        imageTransform,
        backgroundGradient = { colors: [], angle: 90 },
        dynamicInfo,
    } = (layer?.data as IBackgroundLayerProps) || {
        backgroundColor: '',
        url: '',
        stretchOption: StretchOptionType.cover,
        imageTransform: {},
        backgroundGradient: { colors: [], angle: 90 },
    };
    const cacheBackgroundLayer = useRef<ILayerProps | null>(layer);
    const keepCurrentOnColorSwitch = useRef(false);
    const isClickFastColor = useRef<boolean>(false);
    const [activeIndex, setActiveIndex] = useState<TAB_KEY>(url ? TAB_KEY.IMAGE : TAB_KEY.COLOR);
    const [color, setColor] = useState<string>(backgroundColor);
    const [current, setCurrent] = useState<IBackgroundImg | null>(
        !url ? null : { id: Number(materialId), url, stretchOption, imageTransform, dynamicInfo },
    );

    const [lock, setLock] = useState<boolean>(false);

    useDeepCompareEffect(() => {
        if (backgroundGradient && backgroundGradient?.colors?.length > 0) {
            setColor(formatColor(backgroundGradient) as string);
        } else if (backgroundColor) {
            setColor(backgroundColor);
        }
        setActiveIndex(TAB_KEY.COLOR);
    }, [backgroundColor, backgroundGradient]);

    useDeepCompareEffect(() => {
        if (url) {
            setCurrent((previous) => ({
                url,
                id: Number(materialId),
                stretchOption,
                imageTransform,
                // 保留设计师端动态背景草稿，便于从静态图片切回继续制作。
                dynamicInfo: dynamicInfo ?? previous?.dynamicInfo,
            }));
            setActiveIndex(dynamicInfo ? TAB_KEY.DYNAMIC : TAB_KEY.IMAGE);
        } else if (keepCurrentOnColorSwitch.current) {
            keepCurrentOnColorSwitch.current = false;
        } else {
            setCurrent(null);
        }
    }, [url, imageTransform, stretchOption, materialId, dynamicInfo]);

    const canvasType = useTemplateModel((state) => state.canvasType);

    const updateCurrent = (data: IUpdateBackgroundImg) => {
        setCurrent((previous) => ({ ...previous, id: Number(materialId), ...data }) as IBackgroundImg);
    };

    useEffect(() => {
        keepCurrentOnColorSwitch.current = false;
        if (backgroundGradient && backgroundGradient?.colors?.length > 0) {
            setColor(formatColor(backgroundGradient) as string);
        } else {
            setColor(backgroundColor);
        }
        setCurrent(
            !url
                ? null
                : {
                      id: Number(materialId),
                      url: url,
                      stretchOption,
                      imageTransform,
                      dynamicInfo,
                  },
        );
        setActiveIndex(dynamicInfo ? TAB_KEY.DYNAMIC : url ? TAB_KEY.IMAGE : TAB_KEY.COLOR);
    }, [layer?.id]);

    useEffect(() => {
        if (dynamicInfo && dynamicInfo.name) {
            setActiveIndex(TAB_KEY.DYNAMIC);
            return;
        } else if (url) {
            setActiveIndex(TAB_KEY.IMAGE);
            return;
        } else {
            setActiveIndex(TAB_KEY.COLOR);
            return;
        }
    }, [dynamicInfo, url]);

    if (!layer) return null;

    const data: ITabItemProps[] = [
        {
            name: $t((v) => v.panel.backgroundPanel.color),
            content: (
                <>
                    <div className="c-background-panel__color-picker-title">
                        {$t((v) => v.panel.backgroundPanel.currentBgColor)}
                    </div>
                    <ColorGroup
                        popupTriggerSize={{
                            height: 36,
                        }}
                        isGradient={true}
                        tabLock={lock}
                        color={!color || color === 'transparent' ? '#ffffff00' : color}
                        setColor={(val, __, { isBrandColor }) => {
                            setLock(true);
                            cacheBackgroundLayer.current = layer;
                            if (val.includes('linear-gradient')) {
                                editor.backgroundLayer.setBackgroundGradient(
                                    {
                                        url: '',
                                        backgroundColor: '',
                                        backgroundGradient: formatColor(val) as GradientColorResult,
                                    },
                                    false,
                                );
                            } else {
                                editor.backgroundLayer.setBackgroundColor(
                                    {
                                        url: '',
                                        backgroundColor: val,
                                    },
                                    false,
                                );
                            }
                            setColor(val);
                        }}
                        setColorAfter={(val, __, { isBrandColor }) => {
                            if (val.includes('linear-gradient')) {
                                editor.backgroundLayer.setBackgroundGradient(
                                    {
                                        url: '',
                                        backgroundColor: '',
                                        backgroundGradient: formatColor(val) as GradientColorResult,
                                    },
                                    true,
                                    cacheBackgroundLayer.current!,
                                );
                            } else {
                                editor.backgroundLayer.setBackgroundColor(
                                    {
                                        url: '',
                                        backgroundColor: val,
                                    },
                                    true,
                                    cacheBackgroundLayer.current!,
                                );
                            }
                            setColor(val);
                            setLock(false);
                            isClickFastColor.current = false;
                        }}
                    >
                        {({ PopupTrigger, FastPresetBtns }) => (
                            <>
                                {PopupTrigger}
                                <div className="c-background-panel__fast-color-title">
                                    {$t((v) => v.panel.backgroundPanel.recommendColor)}
                                </div>
                                <ul className="c-background-panel__fast-color-container">
                                    {FastPresetBtns.map((btn, index) => (
                                        <li
                                            onMouseDown={() => {
                                                isClickFastColor.current = true;
                                            }}
                                            key={index}
                                        >
                                            {btn.node}
                                            {btn.isActived && (
                                                <IconFont name="CheckBlack" fontSize={14} color="#ffffff" />
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </ColorGroup>
                </>
            ),
            tabKey: TAB_KEY.COLOR,
        },
        {
            name: $t((v) => v.panel.backgroundPanel.image),
            tabKey: TAB_KEY.IMAGE,
            content: (
                <div>
                    {url ? (
                        <>
                            <PreviewWrap url={url} />
                            <BackgroundBtns
                                layer={layer}
                                setCurrent={updateCurrent}
                                currentWidth={currentWidth}
                                currentHeight={currentHeight}
                                selectedIndexes={selectedIndexes}
                            />
                        </>
                    ) : (
                        <div className={classNames('c-background-panel__opt-btn empty', { isIntl })}>
                            <UploadBackground
                                card={true}
                                setCurrent={updateCurrent}
                                currentWidth={currentWidth}
                                currentHeight={currentHeight}
                            />
                        </div>
                    )}
                </div>
            ),
        },
        {
            name: $t((v) => v.panel.backgroundPanel.dynamicPanel.title),
            tabKey: TAB_KEY.DYNAMIC,
            content: <DynamicBg setCurrent={updateCurrent} />,
        },
    ];

    const handleGlobalApply = () => {
        const stages = editor.services.frame.getFrameIdsLength();
        for (let i = 0; i < stages; i++) {
            const bg = editor.backgroundLayer.getBackgroundLayer(i);
            if (!bg) continue;
            if (activeIndex === TAB_KEY.DYNAMIC) applyDynamic(bg);
            if (activeIndex === TAB_KEY.COLOR) applyColor(bg);
            if (activeIndex === TAB_KEY.IMAGE) applyImage(bg);
        }
    };

    const applyDynamic = (backgroundLayer: ILayerProps) => {
        backgroundLayer.data = cloneDeep(layer?.data) as IBackgroundLayerProps;
        backgroundLayer.data = {
            ...backgroundLayer.data,
            dynamicInfo: {
                ...dynamicInfo,
            },
        } as IBackgroundLayerProps;
        editor.backgroundLayer.update(backgroundLayer!, true);
    };

    const applyColor = (backgroundLayer: ILayerProps) => {
        backgroundLayer.data = cloneDeep(layer?.data) as IBackgroundLayerProps;
        if (color && (color !== backgroundColor || url) && color.includes('linear-gradient')) {
            backgroundLayer.data = {
                ...backgroundLayer.data,
                url: '',
                backgroundColor: '',
                backgroundGradient: formatColor(color) as GradientColorResult,
                dynamicInfo: undefined,
            } as IBackgroundLayerProps;
        } else {
            backgroundLayer.data = {
                ...backgroundLayer.data,
                url: '',
                backgroundColor: color,
                dynamicInfo: undefined,
            } as IBackgroundLayerProps;
        }
        editor.backgroundLayer.update(backgroundLayer!, true);
    };

    const applyImage = (backgroundLayer: ILayerProps) => {
        backgroundLayer.data = cloneDeep(layer?.data) as IBackgroundLayerProps;
        backgroundLayer.data = {
            ...backgroundLayer.data,
            url: current?.url,
            backgroundColor: '',
            dynamicInfo: undefined,
        } as IBackgroundLayerProps;
        editor.backgroundLayer.update(backgroundLayer!, true);
    };

    return (
        <>
            <Tabs
                activeIndex={Number(activeIndex)}
                handleTabClick={async (key: string) => {
                    if (lock) return;
                    if (key === TAB_KEY.COLOR) {
                        keepCurrentOnColorSwitch.current = !!url;
                        if (color && (color !== backgroundColor || url) && color.includes('linear-gradient')) {
                            editor.backgroundLayer.setBackgroundGradient({
                                url: '',
                                backgroundColor: '',
                                backgroundGradient: formatColor(color) as GradientColorResult,
                                dynamicInfo: undefined,
                            });
                        } else {
                            editor.backgroundLayer.setBackgroundColor({
                                url: '',
                                backgroundColor: color,
                                dynamicInfo: undefined,
                            });
                        }
                    } else if (key === TAB_KEY.DYNAMIC) {
                        const dynamicInfo = current?.dynamicInfo;
                        if (dynamicInfo) {
                            if ((layer.data as IBackgroundLayerProps).url !== dynamicInfo.screenshotUrl) {
                                (layer.data as IBackgroundLayerProps).url = dynamicInfo.screenshotUrl;
                                const { width, height } = await getImgInfoAsync(dynamicInfo.screenshotUrl);
                                const { widthRatio, heightRatio } = computerBgRatio({
                                    imgWidth: width,
                                    imgHeight: height,
                                    stageHeight: layer.height,
                                    stageWidth: layer.width,
                                });

                                (layer.data as IBackgroundLayerProps) = {
                                    ...(layer.data as IBackgroundLayerProps),
                                    backgroundColor: '',
                                    naturalHeight: height,
                                    naturalWidth: width,
                                    imageTransform: {
                                        offsetX: 0,
                                        offsetY: 0,
                                        rotate: 0,
                                        scale: 1,
                                        flipH: false,
                                        flipV: false,
                                        widthRatio,
                                        heightRatio,
                                    },
                                };
                            }

                            const newLayer = editor.backgroundLayer.generateLayer(layer, {
                                ...layer.data,
                                dynamicInfo: cloneDeep(dynamicInfo),
                            });

                            editor.backgroundLayer.update(newLayer, true, undefined, layer);
                        }
                    } else if (current?.url && (url !== current.url || activeIndex === TAB_KEY.DYNAMIC)) {
                        editor.backgroundLayer.setBackgroundUrl({
                            url: current.url,
                            backgroundColor: '',
                            id: current.id || 0,
                            stretchOption: current.stretchOption,
                            imageTransform: current.imageTransform,
                            dynamicInfo: undefined,
                        });
                    }
                    setActiveIndex(key as TAB_KEY);
                }}
                data={data}
                tabsWidth="228px"
                tabsHeight="34px"
            />
            {(canvasType === CanvasType.PPT ||
                canvasType === CanvasType.BATCH ||
                canvasType === CanvasType.VARIETY) && (
                <div className="operate">
                    <Button type="primary" onClick={handleGlobalApply}>
                        一键换背景
                    </Button>
                </div>
            )}
        </>
    );
};

export default React.memo(TabsRender);
